<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <div class="modal-content">
        <div class="w-full max-w-lg p-6 bg-white shadow-lg rounded-lg">
          <div>
            <h2 class="text-2xl font-semibold mb-4">Generate Report</h2>
            <p class="text-gray-600 text-sm mb-4 flex items-center">
              <span class="material-icons text-gray-500 mr-2">info</span>
              Reports will be ready by the fifth day of each month.
            </p>


            <div class="mb-4">
              <label class="block font-medium text-gray-700 mb-1">Generate as</label>
              <div class="space-y-2">
                <label class="flex items-center">
                  <input type="radio" value="CSV" v-model="fileType" class="form-radio h-4 w-4 text-yellow-600" />
                  <span class="ml-2 text-gray-700">CSV</span>
                </label>
                <label class="flex items-center">
                  <input type="radio" value="PDF" v-model="fileType" class="form-radio h-4 w-4 text-yellow-600" />
                  <span class="ml-2 text-gray-700">PDF</span>
                </label>
              </div>
            </div>


            <div class="mb-4">
              <label class="block font-medium text-gray-700 mb-1">Report Type</label>
              <div class="space-y-2">
                <label class="flex items-center">
                  <input type="radio" value="states_and_units" v-model="reportType"
                    class="form-radio h-4 w-4 text-yellow-600" />
                  <span class="ml-2 text-gray-700">States and units</span>
                </label>
                <label class="flex items-center">
                  <input type="radio" value="states_only" v-model="reportType"
                    class="form-radio h-4 w-4 text-yellow-600" />
                  <span class="ml-2 text-gray-700">States only</span>
                </label>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-gray-700 mb-1">States</label>
                <v-select class="w-full custom-multiple-select" v-model="selectedStates" :options="statesOptions"
                  label="label" :multiple="true" placeholder="Select states" />
              </div>
              <div v-if="reportType === 'states_and_units'">
                <label class="block text-gray-700 mb-1 custom-multiple-select">Vehicles</label>
                <v-select :multiple="true" :options="vehicles" v-model="selectedVehicles" class="w-full"
                  placeholder="Select vehicles" />
              </div>
            </div>

            <div
              :class="(selectedYear && selectedYear.value === 'custom' ? 'grid-cols-3' : 'grid-cols-2') + ' grid gap-4 mb-4'">
              <div>
                <label class="block text-gray-700 mb-1">Year</label>
                <v-select :options="yearOptions" v-model="selectedYear" class="w-full" @input="onYearChange"
                  placeholder="Select year" />
              </div>

              <div v-if="!selectedYear || selectedYear.value !== 'custom'">
                <label class="block text-gray-700 mb-1">Quarter</label>
                <v-select :options="quarterOptions" v-model="selectedQuarter" class="w-full"
                  placeholder="Select quarter" />
              </div>

              <div v-if="selectedYear && selectedYear.value === 'custom'">
                <label class="block text-gray-700 mb-1">From</label>
                <date-picker v-model="customRange.from" placeholder="Start month and year" class="w-full" type="month"
                  :disabled-date="disabledDate" />
              </div>

              <div v-if="selectedYear && selectedYear.value === 'custom'">
                <label class="block text-gray-700 mb-1">To</label>
                <date-picker v-model="customRange.to" placeholder="End month and year" class="w-full" type="month"
                  :disabled-date="disabledDate" />
              </div>
            </div>

            <div class="flex justify-end space-x-4 mt-4">
              <vs-button @click="cancel" class="text-black bg-soft-gray">Cancel</vs-button>
              <vs-button @click="generateReport">Generate</vs-button>
            </div>
            <p v-if="showError" class="text-red mt-4">Something went wrong, please try again later.</p>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css'; // Import CSS for styling
import { STATE_OPTIONS } from '../constants/constants';

export default {
  components: { vSelect, DatePicker },
  data() {
    return {
      showError: false,
      fileType: 'PDF',
      reportType: 'states_and_units',
      selectedStates: null,
      selectedVehicles: null,
      selectedYear: { label: `${new Date().getFullYear()}`, value: `${new Date().getFullYear()}` },
      selectedQuarter: null,
      customRange: {
        from: null,
        to: null
      },
      statesOptions: STATE_OPTIONS,
      yearOptions: [
        { label: "2020", value: "2020" },
        { label: "2021", value: "2021" },
        { label: "2022", value: "2022" },
        { label: "2023", value: "2023" },
        { label: "2024", value: "2024" },
        { label: "2025", value: "2025" },
        { label: "Custom range", value: "custom" },
      ],
      quarterOptions: [
        { label: "1 quarter", value: "Q1" },
        { label: "2 quarter", value: "Q2" },
        { label: "3 quarter", value: "Q3" },
        { label: "4 quarter", value: "Q4" },
      ],

    };
  },
  methods: {
    extractYearAndMonth(date) {
      const dateObject = new Date(date);
      const year = dateObject.getFullYear();
      const month = dateObject.toLocaleString('en-US', { month: 'long' })
      return { year: year, month: month }
    },
    onYearChange() {
      if (this.selectedYear && this.selectedYear.value !== 'custom') {
        this.customRange = { from: null, to: null };
      } else {
        this.selectedQuarter = null;
      }
    },
    cancel() {
      this.$emit('cancel');
    },
    generateReport() {
      this.showError = false;
      const stateValue = this.selectedStates ? this.selectedStates.map((el) => el.value).join(",") : null;
      const vehicleValue = this.selectedVehicles ? this.selectedVehicles.map((el) => el.value).join(",") : null;

      let query = {
        state: stateValue,
        vehicle: vehicleValue,
        year: this.selectedYear && this.selectedYear.value,
        quarter: this.selectedQuarter && this.selectedQuarter.value,
      };

      if (this.customRange.from && this.selectedYear.value === 'custom') {
        delete query.year;
        const from = this.extractYearAndMonth(this.customRange.from)
        const to = this.extractYearAndMonth(this.customRange.to)
        query.start_year = from.year
        query.start_month = from.month
        query.end_year = to.year
        query.end_month = to.month
      }

      if (this.reportType === 'states_only') {
        delete query.vehicle
      }
      this.$vs.loading()


      this.fileType === 'PDF' ?
        this.$store.dispatch("reports/downloadPdfFile", query)

          .catch(() => {
            this.showError = true;
          })
          .finally(() => {
            this.$vs.loading.close();
          }) :
        this.$store.dispatch("reports/downloadCsvFile", query)
          .catch(() => {
            this.showError = true;
          })
          .finally(() => {
            this.$vs.loading.close();
          });
    },
  },
  computed: {
    vehicles() {
      return this.$store.state.reports.vehicles;
    },
  },

}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  animation: fadeIn 0.3s forwards;
}


::v-deep .custom-multiple-select .vs__selected-options {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}

::v-deep .custom-multiple-select .vs__selected-options::-webkit-scrollbar {
  height: 2px;
}

::v-deep .custom-multiple-select .vs__selected-options::-webkit-scrollbar-thumb {
  background-color: #a0a0a0;
  /* Color of the scrollbar thumb */
  border-radius: 0px;
}

::v-deep .custom-multiple-select .vs__selected-options::-webkit-scrollbar-track {
  background-color: #f0f0f0;
  /* Color of the scrollbar track */
}

.modal-container {
  position: relative;
  top: 7%;
  width: 100%;
  max-width: 700px;
  border-radius: 8px;
  transform: translateY(-100%);
  animation: slideIn 0.5s forwards ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-100%);
  }

  to {
    transform: translateY(0);
  }
}
</style>
